import { useEffect, useMemo, useState } from 'react';
import {
  Check,
  CalendarDays,
  ChevronDown,
  Pencil,
  Search,
  ShieldCheck,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { getApiErrorMessage } from '../../../../utils/apiError';
import Layout from '../components/layout/Layout.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';

type BlockedItem = {
  id: number;
  telefone: string;
  nome?: string | null;
  motivo?: string | null;
  created_at?: string | null;
  criado_em?: string | null;
};

const DEFAULT_REASON = 'Atendimento por IA desativado manualmente';

const SUGGESTED_REASONS = [
  'Cliente solicitou atendimento humano',
  'Problema recorrente',
  'Spam / teste',
  'Equipe interna',
  'Outro',
];

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-teal-500 focus:ring-4 focus:ring-teal-600/10';

const formatPhone = (phone: string) => {
  const digits = String(phone || '').replace(/\D/g, '');
  const normalized = digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;

  if (normalized.length === 11) {
    return `(${normalized.slice(0, 2)}) ${normalized.slice(2, 7)}-${normalized.slice(7)}`;
  }

  if (normalized.length === 10) {
    return `(${normalized.slice(0, 2)}) ${normalized.slice(2, 6)}-${normalized.slice(6)}`;
  }

  return phone;
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Sem data';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem data';

  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const getBlockReason = (item: BlockedItem) => item.motivo?.trim() || DEFAULT_REASON;

const getCreatedAt = (item: BlockedItem) => item.created_at || item.criado_em || null;

const parseDateBoundary = (value: string, boundary: 'start' | 'end') => {
  if (!value) return null;
  const time = boundary === 'start' ? '00:00:00.000' : '23:59:59.999';
  const date = new Date(`${value}T${time}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

type RequestError = {
  message?: string;
  response?: {
    status?: number;
    statusText?: string;
    data?: string | {
      error?: string;
      message?: string;
    };
  };
};

const getRequestErrorMessage = (error: unknown) => (
  getApiErrorMessage(error, 'Erro inesperado sem detalhes retornados pela API.')
);

const logRequestError = (context: string, error: unknown) => {
  const requestError = error as RequestError;
  console.error(context, {
    detail: getRequestErrorMessage(error),
    status: requestError.response?.status,
    data: requestError.response?.data,
    error,
  });
};

const InfoTip = ({ text }: { text: string }) => (
  <span className="group relative inline-flex items-center">
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-[11px] font-bold leading-none text-slate-500">
      i
    </span>
    <span className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 hidden w-max max-w-[260px] -translate-x-1/2 whitespace-normal rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium leading-5 text-white shadow-lg group-hover:block">
      {text}
    </span>
  </span>
);

const BlockedNumbers = () => {
  const [blocked, setBlocked] = useState<BlockedItem[]>([]);
  const [input, setInput] = useState('');
  const [singlePhone, setSinglePhone] = useState('');
  const [singleName, setSingleName] = useState('');
  const [singleReason, setSingleReason] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isAddingSingle, setIsAddingSingle] = useState(false);
  const [addMode, setAddMode] = useState<'single' | 'bulk'>('single');
  const [searchTerm, setSearchTerm] = useState('');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTelefone, setEditTelefone] = useState('');
  const [editNome, setEditNome] = useState('');
  const [editMotivo, setEditMotivo] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [reactivateTargetId, setReactivateTargetId] = useState<number | null>(null);
  const [isReactivating, setIsReactivating] = useState(false);

  const fetchBlocked = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get(`/atendabot/bloqueados`);
      setBlocked(data);
    } catch (e) {
      setBlocked([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlocked();
    // Carrega a lista só no mount; `fetchBlocked` usa setState local e não precisa re-disparar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async () => {
    const numeros = Array.from(
      new Set(
        input
          .split(/[\n,; ]+/)
          .map((item) => item.trim())
          .filter(Boolean)
      )
    );

    if (!numeros.length) {
      toast.warning('Digite pelo menos um número.');
      return;
    }

    setIsAdding(true);
    try {
      const motivo = bulkReason.trim();
      await api.post(
        '/atendabot/bloqueados',
        { telefones: numeros, motivo: motivo || null }
      );
      setInput('');
      setBulkReason('');
      setBulkModalOpen(false);
      await fetchBlocked();
    } catch (error) {
      const errorMessage = getRequestErrorMessage(error);
      logRequestError('Erro ao excluir números do atendimento por IA:', error);
      toast.error(`Erro ao excluir números do atendimento por IA. ${errorMessage}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddSingle = async () => {
    const telefone = singlePhone.trim();
    const nome = singleName.trim();
    const motivo = singleReason.trim();

    if (!telefone) {
      toast.warning('Digite um telefone.');
      return;
    }

    setIsAddingSingle(true);
    try {
      await api.post('/atendabot/bloqueados', {
        telefone,
        nome: nome || null,
        motivo: motivo || null
      });
      setSinglePhone('');
      setSingleName('');
      setSingleReason('');
      await fetchBlocked();
    } catch (error) {
      const errorMessage = getRequestErrorMessage(error);
      logRequestError('Erro ao excluir contato do atendimento por IA:', error);
      toast.error(`Erro ao excluir contato do atendimento por IA. ${errorMessage}`);
    } finally {
      setIsAddingSingle(false);
    }
  };

  const handleDelete = (id: number) => {
    setReactivateTargetId(id);
  };

  const handleReactivateConfirm = async () => {
    if (reactivateTargetId == null) return;
    setIsReactivating(true);
    try {
      await api.delete(`/atendabot/bloqueados/${reactivateTargetId}`);
      setReactivateTargetId(null);
      await fetchBlocked();
      toast.success('Atendimento por IA reativado para este contato.');
    } catch (error) {
      const errorMessage = getRequestErrorMessage(error);
      logRequestError('Erro ao reativar contato:', error);
      toast.error(`Erro ao reativar contato. ${errorMessage}`);
    } finally {
      setIsReactivating(false);
    }
  };

  const handleEditStart = (item: BlockedItem) => {
    setEditingId(item.id);
    setEditTelefone(item.telefone || '');
    setEditNome(item.nome || '');
    setEditMotivo(item.motivo || '');
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditTelefone('');
    setEditNome('');
    setEditMotivo('');
  };

  const handleEditSave = async (id: number) => {
    const telefone = editTelefone.trim();
    const nome = editNome.trim();
    const motivo = editMotivo.trim();

    if (!telefone) {
      toast.warning('Digite um telefone.');
      return;
    }

    setIsSaving(true);
    try {
      await api.put(`/atendabot/bloqueados/${id}`, {
        telefone,
        nome: nome || null,
        motivo: motivo || null
      });
      setEditingId(null);
      await fetchBlocked();
    } catch (error) {
      const errorMessage = getRequestErrorMessage(error);
      logRequestError('Erro ao atualizar contato:', error);
      toast.error(`Erro ao atualizar contato. ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredBlocked = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const startBoundary = parseDateBoundary(dateStart, 'start');
    const endBoundary = parseDateBoundary(dateEnd, 'end');

    return blocked.filter((item) => {
      const reason = getBlockReason(item);
      const matchesReason = reasonFilter === 'all' || reason === reasonFilter;
      const matchesSearch =
        !normalizedSearch ||
        item.telefone.toLowerCase().includes(normalizedSearch) ||
        formatPhone(item.telefone).toLowerCase().includes(normalizedSearch) ||
        (item.nome || '').toLowerCase().includes(normalizedSearch) ||
        reason.toLowerCase().includes(normalizedSearch);

      const createdAt = getCreatedAt(item);
      const createdDate = createdAt ? new Date(createdAt) : null;
      const matchesDate =
        (!startBoundary && !endBoundary) ||
        (createdDate !== null &&
          !Number.isNaN(createdDate.getTime()) &&
          (!startBoundary || createdDate >= startBoundary) &&
          (!endBoundary || createdDate <= endBoundary));

      return matchesReason && matchesSearch && matchesDate;
    });
  }, [blocked, dateEnd, dateStart, reasonFilter, searchTerm]);

  const availableReasons = useMemo(() => {
    const reasons = blocked.map((item) => getBlockReason(item));
    return Array.from(new Set(reasons));
  }, [blocked]);

  const activeCountLabel =
    blocked.length === 1 ? '1 exclusão ativa' : `${blocked.length} exclusões ativas`;
  const bulkPreview = input.trim() || '';
  const hasDateFilter = Boolean(dateStart || dateEnd);

  const reactivateTarget = reactivateTargetId != null
    ? blocked.find((item) => item.id === reactivateTargetId) || null
    : null;

  return (
    <Layout>
      <ConfirmModal
        open={reactivateTargetId != null}
        variant="primary"
        title="Reativar atendimento por IA?"
        description="Este contato voltará a ser atendido automaticamente pela IA do Atendabot."
        preview={reactivateTarget ? (
          <div>
            <div className="font-semibold text-slate-900">
              {reactivateTarget.nome || formatPhone(reactivateTarget.telefone)}
            </div>
            {reactivateTarget.nome && (
              <div className="mt-1 text-xs text-slate-500">{formatPhone(reactivateTarget.telefone)}</div>
            )}
          </div>
        ) : null}
        confirmLabel={isReactivating ? 'Reativando...' : 'Reativar IA'}
        saving={isReactivating}
        onCancel={() => { if (!isReactivating) setReactivateTargetId(null); }}
        onConfirm={handleReactivateConfirm}
      />
      <div className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-[1400px] space-y-5">
          <div className="mb-1 text-center">
            <h2 className="flex items-center justify-center gap-3 text-2xl font-black text-slate-900">
              <ShieldCheck size={28} className="text-teal-700" />
              Controle de números atendidos pela IA
            </h2>
            <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-slate-600">
              Defina quais clientes não devem ser atendidos pela IA.
              Esses contatos continuarão podendo enviar mensagens, mas ficarão fora do
              atendimento por IA até serem reativados.
            </p>
          </div>

          <section className="overflow-visible rounded-[20px] border border-slate-200 bg-white shadow-[0_10px_30px_rgba(22,34,66,0.08)]">
            <datalist id="atendabot-block-reason-options">
              {SUGGESTED_REASONS.map((reason) => (
                <option key={reason} value={reason} />
              ))}
            </datalist>

            <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2 text-base font-extrabold text-slate-900">
                  Adicionar cliente à exclusão da IA
                  <InfoTip text="O cliente continuará enviando mensagens, mas ficará fora do atendimento por IA." />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Use um telefone com DDD. Para vários contatos, cole um número por linha ou separado por vírgula.
                </p>
              </div>

              <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={() => setAddMode('single')}
                  className={`h-9 rounded-full px-4 text-xs font-bold transition ${
                    addMode === 'single'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  1 contato
                </button>
                <button
                  type="button"
                  onClick={() => setAddMode('bulk')}
                  className={`h-9 rounded-full px-4 text-xs font-bold transition ${
                    addMode === 'bulk'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Vários números
                </button>
              </div>
            </div>

            <div className="px-5 py-5">
              {addMode === 'single' ? (
                <div className="grid gap-3 lg:grid-cols-[1.1fr_1.1fr_0.9fr_auto] lg:items-end">
                  <label className="space-y-2">
                    <span className="text-sm font-semibold text-slate-600">
                      Nome do cliente <span className="font-medium text-slate-400">(opcional)</span>
                    </span>
                    <input
                      type="text"
                      className={`${inputClass} h-12`}
                      placeholder="Ex.: Ricardo Souza"
                      value={singleName}
                      onChange={(event) => setSingleName(event.target.value)}
                      disabled={isAddingSingle}
                    />
                  </label>

                  <label className="space-y-2">
                    <span className="text-sm font-semibold text-slate-600">Telefone</span>
                    <input
                      type="text"
                      className={`${inputClass} h-12`}
                      placeholder="Ex.: 51999999999"
                      value={singlePhone}
                      onChange={(event) => setSinglePhone(event.target.value)}
                      disabled={isAddingSingle}
                    />
                  </label>

                  <label className="space-y-2">
                    <span className="text-sm font-semibold text-slate-600">
                      Motivo <span className="font-medium text-slate-400">(opcional)</span>
                    </span>
                    <input
                      type="text"
                      list="atendabot-block-reason-options"
                      className={`${inputClass} h-12`}
                      placeholder="Ex.: Cliente solicitou humano"
                      value={singleReason}
                      onChange={(event) => setSingleReason(event.target.value)}
                      disabled={isAddingSingle}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={handleAddSingle}
                    disabled={isAddingSingle}
                    className="h-12 rounded-xl bg-[#2f7d72] px-5 text-sm font-bold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isAddingSingle ? 'Excluindo...' : 'Excluir do atendimento por IA'}
                  </button>
                </div>
              ) : (
                <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(240px,0.8fr)] lg:items-end xl:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.8fr)_auto]">
                  <div className="space-y-2">
                    <span className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                      Lista de números
                      <InfoTip text="Cole um número por linha ou separado por vírgula. Use apenas números com DDD." />
                    </span>
                    <button
                      type="button"
                      className={`${inputClass} flex h-12 items-center text-left disabled:cursor-not-allowed disabled:opacity-60`}
                      onClick={() => setBulkModalOpen(true)}
                      disabled={isAdding}
                    >
                      <span className={bulkPreview ? 'truncate' : 'truncate text-slate-400'}>
                        {bulkPreview || 'Clique para inserir números'}
                      </span>
                    </button>
                  </div>

                  <label className="space-y-2">
                    <span className="text-sm font-semibold text-slate-600">
                      Motivo <span className="font-medium text-slate-400">(opcional)</span>
                    </span>
                    <input
                      type="text"
                      list="atendabot-block-reason-options"
                      className={`${inputClass} h-12`}
                      placeholder="Ex.: Equipe interna"
                      value={bulkReason}
                      onChange={(event) => setBulkReason(event.target.value)}
                      disabled={isAdding}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={handleAdd}
                    disabled={isAdding}
                    className="h-12 w-full rounded-xl bg-[#2f7d72] px-5 text-sm font-bold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60 lg:col-span-2 xl:col-span-1 xl:w-auto xl:whitespace-nowrap"
                  >
                    {isAdding ? 'Excluindo...' : 'Excluir números do atendimento por IA'}
                  </button>
                </div>
              )}

              <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
                    Clientes fora do atendimento por IA
                    <InfoTip text="Reative para voltar o atendimento por IA." />
                  </h3>
                </div>
                <div className="inline-flex w-fit rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-500">
                  {activeCountLabel}
                </div>
              </div>

              <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_260px]">
                <div className="relative">
                  <Search
                    size={18}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou telefone"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className={`${inputClass} h-12 pl-11`}
                  />
                </div>

                <select
                  value={reasonFilter}
                  onChange={(event) => setReasonFilter(event.target.value)}
                  className={`${inputClass} h-12`}
                >
                  <option value="all">Todos os motivos</option>
                  {availableReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-3 rounded-2xl border border-slate-100 bg-slate-50/70">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-bold text-slate-700 transition hover:text-slate-900"
                  onClick={() => setDateFilterOpen((isOpen) => !isOpen)}
                  aria-expanded={dateFilterOpen}
                  aria-controls="blocked-date-filter"
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarDays size={16} className="text-teal-700" />
                    Filtrar por data de inclusão
                  </span>
                  <span className="inline-flex items-center gap-2">
                    {hasDateFilter && (
                      <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-extrabold text-teal-700 ring-1 ring-teal-100">
                        ativo
                      </span>
                    )}
                    <ChevronDown
                      size={18}
                      className={`text-slate-400 transition-transform ${dateFilterOpen ? 'rotate-180' : ''}`}
                    />
                  </span>
                </button>

                {dateFilterOpen && (
                  <div
                    id="blocked-date-filter"
                    className="grid gap-3 border-t border-slate-100 px-4 pb-4 pt-3 md:grid-cols-[1fr_1fr_auto] md:items-end"
                  >
                    <label className="space-y-2">
                      <span className="text-sm font-semibold text-slate-600">Data inicial</span>
                      <input
                        type="date"
                        className={`${inputClass} h-12`}
                        value={dateStart}
                        max={dateEnd || undefined}
                        onChange={(event) => setDateStart(event.target.value)}
                      />
                    </label>

                    <label className="space-y-2">
                      <span className="text-sm font-semibold text-slate-600">Data final</span>
                      <input
                        type="date"
                        className={`${inputClass} h-12`}
                        value={dateEnd}
                        min={dateStart || undefined}
                        onChange={(event) => setDateEnd(event.target.value)}
                      />
                    </label>

                    <button
                      type="button"
                      className="h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => {
                        setDateStart('');
                        setDateEnd('');
                      }}
                      disabled={!hasDateFilter}
                    >
                      Limpar período
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-5 space-y-3">
                {isLoading ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-12 text-center text-sm text-slate-500">
                    Carregando contatos...
                  </div>
                ) : blocked.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-12 text-center">
                    <ShieldCheck size={32} className="mx-auto text-slate-400" />
                    <h4 className="mt-3 text-base font-bold text-slate-800">
                      Nenhum cliente fora do atendimento por IA
                    </h4>
                    <p className="mt-1 text-sm text-slate-500">
                      Quando você excluir um contato da IA, ele aparecerá aqui.
                    </p>
                  </div>
                ) : filteredBlocked.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-5 py-12 text-center text-sm text-slate-500">
                    Nenhum cliente encontrado com os filtros atuais.
                  </div>
                ) : (
                  filteredBlocked.map((item) => {
                    const isEditing = editingId === item.id;
                    const displayName = item.nome?.trim() || item.telefone;

                    return (
                      <div
                        key={item.id}
                        className="grid gap-4 rounded-[18px] border border-slate-200 bg-white p-4 shadow-[0_10px_30px_rgba(22,34,66,0.08)] md:grid-cols-[auto_1fr_auto] md:items-center"
                      >
                        <div className="hidden h-14 w-14 place-items-center rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white text-slate-500 md:grid">
                          {item.nome ? <User size={22} /> : <span className="text-lg font-black">#</span>}
                        </div>

                        <div className="min-w-0">
                          {isEditing ? (
                            <div className="grid gap-2 lg:grid-cols-3">
                              <input
                                type="text"
                                className={`${inputClass} h-11`}
                                placeholder="Nome do cliente (opcional)"
                                value={editNome}
                                onChange={(event) => setEditNome(event.target.value)}
                                disabled={isSaving}
                              />
                              <input
                                type="text"
                                className={`${inputClass} h-11 font-mono`}
                                placeholder="Telefone"
                                value={editTelefone}
                                onChange={(event) => setEditTelefone(event.target.value)}
                                disabled={isSaving}
                              />
                              <input
                                type="text"
                                list="atendabot-block-reason-options"
                                className={`${inputClass} h-11`}
                                placeholder="Motivo (opcional)"
                                value={editMotivo}
                                onChange={(event) => setEditMotivo(event.target.value)}
                                disabled={isSaving}
                              />
                            </div>
                          ) : (
                            <>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <div className="text-base font-extrabold text-slate-900">
                                  {displayName}
                                </div>
                                <div className="text-sm font-medium text-slate-500">
                                  {formatPhone(item.telefone)}
                                </div>
                              </div>

                              <div className="mt-3 grid gap-3 sm:grid-cols-[1.6fr_0.8fr]">
                                <div>
                                  <div className="text-xs text-slate-400">Motivo</div>
                                  <div className="mt-1 text-sm font-semibold text-slate-800">
                                    {getBlockReason(item)}
                                  </div>
                                </div>
                                <div className="sm:text-center">
                                  <div className="text-xs text-slate-400">Adicionado em</div>
                                  <div className="mt-1 text-sm font-semibold text-slate-800">
                                    {formatDate(getCreatedAt(item))}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleEditSave(item.id)}
                                disabled={isSaving}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#2f7d72] px-3 text-xs font-semibold text-white transition hover:bg-[#27685f] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <Check size={14} />
                                Salvar
                              </button>
                              <button
                                type="button"
                                onClick={handleEditCancel}
                                disabled={isSaving}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <X size={14} />
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => handleDelete(item.id)}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-emerald-50 px-3 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 transition hover:bg-emerald-100"
                              >
                                <Check size={14} />
                                Reativar IA
                              </button>
                              <button
                                type="button"
                                onClick={() => handleEditStart(item)}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 hover:border-slate-300"
                              >
                                <Pencil size={14} />
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(item.id)}
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 hover:border-rose-300"
                              >
                                <Trash2 size={14} />
                                Excluir
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {bulkModalOpen && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 px-4 py-6">
          <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h4 className="text-base font-extrabold text-slate-900">Inserir lista de números</h4>
              <button
                type="button"
                onClick={() => setBulkModalOpen(false)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50"
              >
                Fechar
              </button>
            </div>

            <label className="space-y-2">
              <span className="text-sm font-semibold text-slate-600">Números (um por linha)</span>
              <textarea
                className={`${inputClass} min-h-[170px] py-3`}
                placeholder={`Ex.:\n11999999999\n51988888888`}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                autoFocus
              />
            </label>
            <p className="mt-2 text-xs leading-5 text-slate-500">
              Cole um número por linha ou separado por vírgula. O sistema remove duplicados antes de salvar.
            </p>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setBulkModalOpen(false)}
                className="rounded-xl bg-[#2f7d72] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#27685f]"
              >
                Inserir
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default BlockedNumbers;
